import { useState } from "react";
import { Link } from "react-router";

import { Button, buttonClassName } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import type { RequestPasswordResetResult } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";

import { AuthScreenLayout } from "./AuthScreenLayout";

/**
 * `/forgot-password` (gap-fill spec, screen 3) — `POST
 * /auth/request-password-reset`. No backend-specified string exists for
 * this one screen alone (judgement call 4): every other screen's messages
 * are the backend's own verbatim sentences, but this route's handler
 * (`app/domain/identity/password_reset.py::request_password_reset`)
 * returns nothing at all by design — there is no message to reuse.
 *
 * `POST /auth/request-password-reset` always returns `{requested: true}`,
 * whatever the address (real, unknown, invited-but-no-password, removed, or
 * the reserved system address — VC-1.8-01). This screen renders the exact
 * same confirmation every time regardless: it never adds its own
 * differentiation on top of a backend that deliberately has none
 * (AC, VC-GF1-07). The copy states what happens next as fact, without
 * implying either confirmation or denial that the address has an account.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [requested, setRequested] = useState(false);

  const canSubmit = email.trim() !== "";

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      await apiFetch<RequestPasswordResetResult>("/auth/request-password-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      setRequested(true);
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  if (requested) {
    return (
      <AuthScreenLayout title="Check your email">
        <p className="text-body text-ink-secondary">
          If that address has a PropDesk account, a reset link is on its way. It will be valid for a limited
          time — check your inbox and follow the link to set a new password.
        </p>
        <Link to="/sign-in" className={buttonClassName("secondary")}>
          Back to sign in
        </Link>
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout title="Forgot your password?">
      <p className="text-body text-ink-secondary">
        Enter the email address on your PropDesk account and we&apos;ll send a link to set a new password.
      </p>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("email")}</span>
          <Input
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        {error ? (
          <p role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <Button type="submit" disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? "Sending…" : "Send reset link"}
        </Button>
      </form>

      <p className="text-meta text-ink-secondary">
        <Link to="/sign-in" className="underline hover:text-ink">
          Back to sign in
        </Link>
      </p>
    </AuthScreenLayout>
  );
}
